import { useEffect, useState } from "react";
import axios from "axios";
import ForecastCard from "@/components/ForecastCard";
import ExportDownloadButton from "@/components/ExportDownloadButton";
import { useAccountant } from "@/contexts/AccountantContext";
import { formatMoneyCompact } from "@/lib/formatMoney";
import { localDateKey } from "@/lib/formatDate";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import KipaIcon from "@/components/kipa/KipaIcon";
import {
  CircleDollarSign,
  FileSpreadsheet,
  FileText,
  Receipt,
  TrendingUp,
  Trophy,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#0D6E6E", "#166534", "#C49A2A", "#991B1B", "#92400E", "#1C1917"];

function ExportBar() {
  return (
    <div className="bg-white border border-[var(--br)] rounded-2xl p-3 mb-4 card-sh" data-testid="export-bar">
      <div className="text-[var(--text-sm)] font-extrabold text-[var(--color-text-primary)] mb-2">Download Reports</div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <ExportDownloadButton
          url={`${API}/export/excel`}
          defaultFilename="kipabiz-export.xlsx"
          testId="export-excel"
          className="w-full text-center py-2.5 rounded-xl bg-[var(--gn)] text-white text-[12px] font-extrabold inline-flex items-center justify-center gap-1.5"
        >
          <KipaIcon icon={FileSpreadsheet} size="sm" color="#fff" />
          Full Excel Bundle
        </ExportDownloadButton>
        <ExportDownloadButton
          url={`${API}/export/csv?type=transactions`}
          defaultFilename="kipabiz-transactions.csv"
          testId="export-csv-txn"
          className="w-full text-center py-2.5 rounded-xl bg-[var(--bll)] border border-[var(--bld)] text-[var(--bl)] text-[12px] font-extrabold inline-flex items-center justify-center gap-1.5"
        >
          <KipaIcon icon={FileText} size="sm" color="var(--bl)" />
          Transactions CSV
        </ExportDownloadButton>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <ExportDownloadButton
          url={`${API}/export/csv?type=invoices`}
          defaultFilename="kipabiz-invoices.csv"
          testId="export-csv-invoices"
          className="w-full text-center py-2 rounded-xl bg-[var(--sf2)] border border-[var(--br)] text-[var(--tx2)] text-[11px] font-bold"
        >
          Invoices
        </ExportDownloadButton>
        <ExportDownloadButton
          url={`${API}/export/csv?type=customers`}
          defaultFilename="kipabiz-customers.csv"
          className="w-full text-center py-2 rounded-xl bg-[var(--sf2)] border border-[var(--br)] text-[var(--tx2)] text-[11px] font-bold"
        >
          Customers
        </ExportDownloadButton>
        <ExportDownloadButton
          url={`${API}/export/csv?type=inventory`}
          defaultFilename="kipabiz-inventory.csv"
          className="w-full text-center py-2 rounded-xl bg-[var(--sf2)] border border-[var(--br)] text-[var(--tx2)] text-[11px] font-bold"
        >
          Inventory
        </ExportDownloadButton>
      </div>
    </div>
  );
}

export default function ReportsScreen({ reloadKey }) {
  const { fmt, currency } = useAccountant();
  const fmtShort = (n) => formatMoneyCompact(n, currency);
  const [txns, setTxns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("month"); // 'day' | 'week' | 'month' | 'year'

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/transactions?limit=1000`),
      axios.get(`${API}/dashboard`),
    ])
      .then(([t, d]) => {
        setTxns(t.data);
        setSummary(d.data);
      })
      .catch(() => {});
  }, [reloadKey]);

  const trendData = buildTrend(txns, period);
  const categoryData = buildCategories(txns, "expense");
  const incomeCategoryData = buildCategories(txns, "income");

  return (
    <div className="flex flex-col h-full bg-[var(--bg2)]" data-testid="reports-screen">
      <div className="bg-white px-4 py-2 border-b border-[var(--br)] flex gap-2 overflow-x-auto">
        {[
          { k: "day", label: "Daily" },
          { k: "week", label: "Weekly" },
          { k: "month", label: "Monthly" },
          { k: "year", label: "Yearly" },
        ].map((p) => (
          <button
            key={p.k}
            onClick={() => setPeriod(p.k)}
            data-testid={`report-period-${p.k}`}
            className={`chip ${period === p.k ? "chip-active" : ""}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <ExportBar />
        <ForecastCard reloadKey={reloadKey} />
        {summary && (          <div className="bg-[var(--ac)] rounded-3xl p-4 mb-4 text-white relative overflow-hidden" style={{ boxShadow: "0 8px 32px rgba(13,110,110,0.28)" }}>
            <div className="absolute -top-7 -right-7 w-32 h-32 rounded-full bg-white/10" />
            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/70 mb-1">This Month — Net Profit</div>
              <div className="text-[34px] font-black leading-none mb-1">{fmt(summary.month.net_profit)}</div>
              <div className="text-[12px] text-white/80 font-semibold mb-3">
                {summary.month.growth_vs_prev_month >= 0 ? "↑" : "↓"} {Math.abs(summary.month.growth_vs_prev_month).toFixed(1)}% vs last month
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white/15 rounded-xl p-2">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-white/60">Revenue</div>
                  <div className="text-[14px] font-extrabold">{fmt(summary.month.revenue)}</div>
                </div>
                <div className="bg-white/15 rounded-xl p-2">
                  <div className="text-[9px] font-bold uppercase tracking-wider text-white/60">Expenses</div>
                  <div className="text-[14px] font-extrabold">{fmt(summary.month.expenses)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trend chart */}
        <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-4 card-sh">
          <div className="text-[13px] font-extrabold text-[var(--tx)] mb-3 flex items-center gap-1.5">
            <KipaIcon icon={TrendingUp} size="sm" color="var(--color-primary)" />
            Profit Trend
          </div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <AreaChart data={trendData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="profitFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0D6E6E" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#0D6E6E" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8A847E" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#8A847E" }} tickFormatter={fmtShort} axisLine={false} tickLine={false} width={40} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5DF", fontSize: 12 }} />
                <Area type="monotone" dataKey="profit" stroke="#0D6E6E" strokeWidth={2.5} fill="url(#profitFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Revenue vs Expenses bar */}
        <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-4 card-sh">
          <div className="text-[13px] font-extrabold text-[var(--tx)] mb-3 flex items-center gap-1.5">
            <KipaIcon icon={CircleDollarSign} size="sm" color="var(--color-primary)" />
            Revenue vs Expenses
          </div>
          <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
              <BarChart data={trendData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#8A847E" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#8A847E" }} tickFormatter={fmtShort} axisLine={false} tickLine={false} width={40} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5DF", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" fill="#166534" radius={[6, 6, 0, 0]} />
                <Bar dataKey="expenses" fill="#991B1B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Category donut */}
        {(categoryData.length > 0 || incomeCategoryData.length > 0) && (
          <div className="grid grid-cols-1 gap-4">
            {incomeCategoryData.length > 0 && (
              <CategoryChart title="Revenue by Category" titleIcon={Trophy} data={incomeCategoryData} fmt={fmt} />
            )}
            {categoryData.length > 0 && (
              <CategoryChart title="Expenses by Category" titleIcon={Receipt} data={categoryData} fmt={fmt} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryChart({ title, titleIcon, data, fmt }) {
  return (
    <div className="bg-white border border-[var(--br)] rounded-2xl p-4 card-sh">
      <div className="text-[13px] font-extrabold text-[var(--tx)] mb-3 flex items-center gap-1.5">
        {titleIcon && <KipaIcon icon={titleIcon} size="sm" color="var(--color-primary)" />}
        {title}
      </div>
      <div className="flex items-center gap-3">
        <div style={{ width: 140, height: 140, flexShrink: 0 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={38} outerRadius={65} paddingAngle={3}>
                {data.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E7E5DF", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 min-w-0">
          {data.slice(0, 5).map((d, i) => (
            <div key={d.name} className="flex items-center gap-2 mb-1.5">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
              <div className="text-[12px] text-[var(--tx)] font-semibold flex-1 truncate capitalize">{d.name}</div>
              <div className="text-[11px] text-[var(--tx2)] font-bold">{fmt(d.value)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────────────────
function buildTrend(txns, period) {
  const now = new Date();
  let buckets;
  if (period === "day") {
    // 6 × 4-hour slots of today
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);
    const iso = todayStart.toISOString().slice(0, 10);
    buckets = Array.from({ length: 6 }, (_, i) => ({
      key: `${iso}T${String(i * 4).padStart(2, "0")}`,
      label: `${i * 4}-${i * 4 + 4}h`,
      revenue: 0, expenses: 0, profit: 0,
    }));
  } else if (period === "week") {
    buckets = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() - (6 - i));
      return {
        key: d.toISOString().slice(0, 10),
        label: d.toLocaleDateString("en-NG", { weekday: "short" }),
        revenue: 0, expenses: 0, profit: 0,
      };
    });
  } else if (period === "month") {
    buckets = Array.from({ length: 30 }, (_, i) => {
      const d = new Date(now);
      d.setDate(d.getDate() - (29 - i));
      return {
        key: d.toISOString().slice(0, 10),
        label: i % 5 === 0 ? d.toLocaleDateString("en-NG", { day: "numeric", month: "short" }) : "",
        revenue: 0, expenses: 0, profit: 0,
      };
    });
  } else {
    buckets = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now);
      d.setMonth(d.getMonth() - (11 - i));
      d.setDate(1);
      return {
        key: d.toISOString().slice(0, 7),
        label: d.toLocaleDateString("en-NG", { month: "short" }),
        revenue: 0, expenses: 0, profit: 0,
      };
    });
  }

  const keyOf = (iso) => {
    if (period === "year") return iso.slice(0, 7);
    if (period === "day") {
      const h = parseInt(iso.slice(11, 13) || "0", 10);
      const slot = Math.floor(h / 4) * 4;
      return `${localDateKey(iso)}T${String(slot).padStart(2, "0")}`;
    }
    return localDateKey(iso);
  };
  const map = new Map(buckets.map((b) => [b.key, b]));

  txns.forEach((t) => {
    const k = keyOf(t.created_at);
    const b = map.get(k);
    if (!b) return;
    if (t.type === "income") {
      b.revenue += t.amount;
      b.profit += t.profit != null ? t.profit : t.amount;
    } else {
      b.expenses += t.amount;
      b.profit -= t.amount;
    }
  });

  return buckets;
}

function buildCategories(txns, type) {
  const map = {};
  txns.forEach((t) => {
    if (t.type !== type) return;
    const c = t.category || "other";
    map[c] = (map[c] || 0) + t.amount;
  });
  return Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}
